import { z } from "zod";
import { InvitationCreateIn, InvitationOut } from "@/lib/api/schemas/invitation";
import { SuccessOut } from "@/lib/api/schemas/shared";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

const InvitationsListParameters = z.object({
  client_id: z.number().int().nullish(),
  search: z.string().nullish(),
  ordering: z.string().nullish(),
  limit: z.number().int().min(1).nullish(),
  offset: z.number().int().min(0).nullish(),
});

/** Invitation management (auth-only) — list, create and cancel invitations for a client. */
class InvitationsEndpoint {
  public static readonly basePath = "invitations";
  public static readonly schemas = {
    list: {
      parameters: InvitationsListParameters,
      response: z.object({
        count: z.number(),
        items: InvitationOut.array(),
      }),
    },
    create: {
      body: InvitationCreateIn,
      response: InvitationOut,
    },
    cancel: {
      response: SuccessOut,
    },
    resend: {
      response: InvitationOut,
    },
  };

  constructor(private api: Auth) {}

  public async list(
    query: z.input<typeof InvitationsEndpoint.schemas.list.parameters> = {},
  ): ActionReturnType<z.infer<typeof InvitationsEndpoint.schemas.list.response>> {
    const params = validateInput(InvitationsEndpoint.schemas.list.parameters, query);
    if (!params.ok) return params;
    return request({
      log: ["Invitations", "list"],
      method: "get",
      path: [InvitationsEndpoint.basePath],
      query: params.data,
      response: InvitationsEndpoint.schemas.list.response,
    });
  }

  public async create(
    body: z.input<typeof InvitationsEndpoint.schemas.create.body>,
  ): ActionReturnType<z.infer<typeof InvitationsEndpoint.schemas.create.response>> {
    const bodyValidated = validateInput(InvitationsEndpoint.schemas.create.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Invitations", "create"],
      method: "post",
      path: [InvitationsEndpoint.basePath],
      json: bodyValidated.data,
      response: InvitationsEndpoint.schemas.create.response,
    });
  }

  public cancel(
    invitationId: number | string,
  ): ActionReturnType<z.infer<typeof InvitationsEndpoint.schemas.cancel.response>> {
    return request({
      log: ["Invitations", "cancel"],
      method: "post",
      path: [InvitationsEndpoint.basePath, invitationId, "cancel"],
      response: InvitationsEndpoint.schemas.cancel.response,
    });
  }

  public resend(
    invitationId: number | string,
  ): ActionReturnType<z.infer<typeof InvitationsEndpoint.schemas.resend.response>> {
    return request({
      log: ["Invitations", "resend"],
      method: "post",
      path: [InvitationsEndpoint.basePath, invitationId, "resend"],
      response: InvitationsEndpoint.schemas.resend.response,
    });
  }
}

export { InvitationsEndpoint };
